/**
 * File explorer component for browsing volume contents.
 * Matches the Sandbox FileExplorer UI pattern.
 *
 * @module components/VolumeFileExplorer
 */

import { useState, useRef, useCallback, useMemo, DragEvent } from 'react';
import { createPortal } from 'react-dom';
import {
  FolderOpen,
  File,
  ChevronRight,
  Upload,
  Download,
  Trash2,
  FolderPlus,
  RefreshCw,
  Loader2,
  AlertCircle,
  MoreHorizontal,
  X,
  Check,
  CheckCircle,
  Circle,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { Button, Modal, Input, ConfirmModal } from '@/components/ui';
import {
  useVolumeDirectory,
  useUploadVolumeFile,
  useDeleteVolumePath,
  useCreateVolumeDirectory,
} from '@/hooks';
import { volumeFileService } from '@/services';
import type { VolumePathItem } from '@/types';

type SortColumn = 'name' | 'size' | 'modified';
type SortDirection = 'asc' | 'desc';

interface VolumeFileExplorerProps {
  volumeName: string;
  groupId?: string;
}

/**
 * Format file size in human-readable format
 */
function formatFileSize(bytes: number | undefined): string {
  if (bytes === undefined || bytes === null) return '-';
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

/**
 * Format date in a readable format
 */
function formatDate(dateString: string | undefined): string {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    return (
      date.toLocaleDateString() +
      ' ' +
      date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    );
  } catch {
    return '-';
  }
}

/**
 * Get file icon for a volume item
 */
function getFileIcon(item: VolumePathItem) {
  if (item.isDirectory) {
    return <FolderOpen className="h-4 w-4 text-yellow-500" />;
  }
  return <File className="h-4 w-4 text-gray-400" />;
}

export function VolumeFileExplorer({ volumeName, groupId }: VolumeFileExplorerProps) {
  const [currentPath, setCurrentPath] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<VolumePathItem[]>([]);
  const [dropdownFile, setDropdownFile] = useState<VolumePathItem | null>(null);
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<VolumePathItem[] | null>(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [dragOverFolder, setDragOverFolder] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [overwrite, setOverwrite] = useState(true);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [sortColumn, setSortColumn] = useState<SortColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [dropdownPos, setDropdownPos] = useState<{ top: number; right: number } | null>(null);
  const [uploadProgress, setUploadProgress] = useState<{
    fileIndex: number;
    fileCount: number;
    currentFileName: string;
    bytesLoaded: number;
    bytesTotal: number;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const uploadAbortRef = useRef<AbortController | null>(null);
  const lastClickedIndexRef = useRef<number>(-1);

  const {
    data: directory,
    isLoading,
    error,
    refetch,
  } = useVolumeDirectory(volumeName, currentPath, groupId);
  const uploadMutation = useUploadVolumeFile(volumeName, groupId);
  const deleteMutation = useDeleteVolumePath(volumeName, groupId);
  const createDirMutation = useCreateVolumeDirectory(volumeName, groupId);

  const toggleSort = useCallback(
    (column: SortColumn) => {
      setSortColumn((prev) => {
        if (prev === column) {
          if (sortDirection === 'asc') {
            setSortDirection('desc');
            return column;
          } else {
            // Third click: clear sort
            setSortDirection('asc');
            return null;
          }
        } else {
          setSortDirection('asc');
          return column;
        }
      });
    },
    [sortDirection]
  );

  // Sort entries: directories always first, then by selected column (default: name asc)
  const sortedEntries = useMemo(() => {
    const items = [...(directory?.items || [])];
    const dir = sortDirection === 'asc' ? 1 : -1;
    return items.sort((a, b) => {
      // Directories always above files
      if (a.isDirectory !== b.isDirectory) return a.isDirectory ? -1 : 1;
      if (!sortColumn) return a.itemName.localeCompare(b.itemName);
      switch (sortColumn) {
        case 'name':
          return dir * a.itemName.localeCompare(b.itemName);
        case 'size':
          return dir * ((a.sizeBytes ?? 0) - (b.sizeBytes ?? 0));
        case 'modified': {
          const aTime = a.lastModifiedUtc ? new Date(a.lastModifiedUtc).getTime() : 0;
          const bTime = b.lastModifiedUtc ? new Date(b.lastModifiedUtc).getTime() : 0;
          return dir * (aTime - bTime);
        }
        default:
          return 0;
      }
    });
  }, [directory?.items, sortColumn, sortDirection]);

  // Navigate to a directory
  const navigateTo = useCallback((path: string) => {
    setCurrentPath(path);
    setSelectedFiles([]);
    setDropdownFile(null);
    setDropdownPos(null);
    lastClickedIndexRef.current = -1;
  }, []);

  // Navigate up one directory
  const navigateUp = useCallback(() => {
    const parts = currentPath.split('/').filter(Boolean);
    parts.pop();
    navigateTo(parts.join('/'));
  }, [currentPath, navigateTo]);

  // Handle file/folder click with multi-select support
  const handleEntryClick = useCallback(
    (item: VolumePathItem, event: React.MouseEvent) => {
      const isModifier = event.ctrlKey || event.metaKey;
      const isShift = event.shiftKey;

      // Plain click on directory navigates
      if (item.isDirectory && !isModifier && !isShift) {
        navigateTo(item.path);
        return;
      }

      const itemIndex = sortedEntries.findIndex((e) => e.path === item.path);

      if (isShift && lastClickedIndexRef.current >= 0) {
        // Range select
        const start = Math.min(lastClickedIndexRef.current, itemIndex);
        const end = Math.max(lastClickedIndexRef.current, itemIndex);
        setSelectedFiles(sortedEntries.slice(start, end + 1));
      } else if (isModifier) {
        // Toggle individual item
        setSelectedFiles((prev) => {
          const exists = prev.some((f) => f.path === item.path);
          return exists ? prev.filter((f) => f.path !== item.path) : [...prev, item];
        });
        lastClickedIndexRef.current = itemIndex;
      } else {
        // Single select (toggle)
        setSelectedFiles((prev) => (prev.length === 1 && prev[0].path === item.path ? [] : [item]));
        lastClickedIndexRef.current = itemIndex;
      }

      setDropdownFile(null);
      setDropdownPos(null);
    },
    [navigateTo, sortedEntries]
  );

  // Handle file upload
  const handleUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files || files.length === 0) return;

      const fileList = Array.from(files);
      const abortController = new AbortController();
      uploadAbortRef.current = abortController;
      const skipped: string[] = [];

      try {
        for (let i = 0; i < fileList.length; i++) {
          if (abortController.signal.aborted) break;
          const file = fileList[i];
          setUploadProgress({
            fileIndex: i,
            fileCount: fileList.length,
            currentFileName: file.name,
            bytesLoaded: 0,
            bytesTotal: file.size,
          });
          const filePath = currentPath ? `${currentPath}/${file.name}` : file.name;
          try {
            await uploadMutation.mutateAsync({
              path: filePath,
              file,
              overwrite,
              signal: abortController.signal,
              onUploadProgress: (progressEvent) => {
                setUploadProgress((prev) =>
                  prev
                    ? {
                        ...prev,
                        bytesLoaded: progressEvent.loaded,
                        bytesTotal: progressEvent.total ?? file.size,
                      }
                    : null
                );
              },
            });
          } catch (err: unknown) {
            const status =
              (err as { status?: number })?.status ??
              (err as { response?: { status?: number } })?.response?.status;
            if (status === 409) {
              skipped.push(file.name);
            } else {
              throw err;
            }
          }
        }
      } catch {
        // Upload was cancelled or failed
      } finally {
        uploadAbortRef.current = null;
        setUploadProgress(null);

        if (skipped.length > 0) {
          setUploadError(
            skipped.length === 1
              ? `"${skipped[0]}" already exists. Enable overwrite to replace.`
              : `${skipped.length} files already exist: ${skipped.join(', ')}. Enable overwrite to replace.`
          );
        }

        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }

        refetch();
      }
    },
    [currentPath, uploadMutation, refetch, overwrite]
  );

  // Upload files to a specific path (for drag-and-drop)
  const uploadFilesToPath = useCallback(
    async (files: FileList, targetPath: string) => {
      const fileList = Array.from(files);
      const abortController = new AbortController();
      uploadAbortRef.current = abortController;
      const skipped: string[] = [];

      try {
        for (let i = 0; i < fileList.length; i++) {
          if (abortController.signal.aborted) break;
          const file = fileList[i];
          setUploadProgress({
            fileIndex: i,
            fileCount: fileList.length,
            currentFileName: file.name,
            bytesLoaded: 0,
            bytesTotal: file.size,
          });
          const filePath = targetPath ? `${targetPath}/${file.name}` : file.name;
          try {
            await uploadMutation.mutateAsync({
              path: filePath,
              file,
              overwrite,
              signal: abortController.signal,
              onUploadProgress: (progressEvent) => {
                setUploadProgress((prev) =>
                  prev
                    ? {
                        ...prev,
                        bytesLoaded: progressEvent.loaded,
                        bytesTotal: progressEvent.total ?? file.size,
                      }
                    : null
                );
              },
            });
          } catch (err: unknown) {
            const status =
              (err as { status?: number })?.status ??
              (err as { response?: { status?: number } })?.response?.status;
            if (status === 409) {
              skipped.push(file.name);
            } else {
              throw err;
            }
          }
        }
      } catch {
        // Upload was cancelled or failed
      } finally {
        uploadAbortRef.current = null;
        setUploadProgress(null);

        if (skipped.length > 0) {
          setUploadError(
            skipped.length === 1
              ? `"${skipped[0]}" already exists. Enable overwrite to replace.`
              : `${skipped.length} files already exist: ${skipped.join(', ')}. Enable overwrite to replace.`
          );
        }

        refetch();
      }
    },
    [uploadMutation, refetch, overwrite]
  );

  // Drag and drop handlers
  const handleDragEnter = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropZoneRef.current && !dropZoneRef.current.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
      setDragOverFolder(null);
    }
  }, []);

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    async (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      setDragOverFolder(null);

      const files = e.dataTransfer.files;
      if (!files || files.length === 0) return;

      const targetPath = dragOverFolder || currentPath;
      await uploadFilesToPath(files, targetPath);
    },
    [currentPath, dragOverFolder, uploadFilesToPath]
  );

  const handleFolderDragOver = useCallback(
    (e: DragEvent<HTMLTableRowElement>, folderPath: string) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOverFolder(folderPath);
    },
    []
  );

  const handleFolderDragLeave = useCallback((e: DragEvent<HTMLTableRowElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverFolder(null);
  }, []);

  const handleFolderDrop = useCallback(
    async (e: DragEvent<HTMLTableRowElement>, folderPath: string) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      setDragOverFolder(null);

      const files = e.dataTransfer.files;
      if (!files || files.length === 0) return;

      await uploadFilesToPath(files, folderPath);
    },
    [uploadFilesToPath]
  );

  // Handle file download (supports single or multi)
  const handleDownload = useCallback(
    async (file?: VolumePathItem | null) => {
      const targets = file ? [file] : selectedFiles.filter((f) => !f.isDirectory);
      if (targets.length === 0) return;

      setIsDownloading(true);
      try {
        for (const target of targets) {
          const { blob, fileName } = await volumeFileService.downloadFile(
            volumeName,
            target.path,
            groupId
          );
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }
      } catch (err) {
        console.error('Download failed:', err);
      } finally {
        setIsDownloading(false);
      }
    },
    [selectedFiles, volumeName, groupId]
  );

  // Handle delete (supports single or multi)
  const handleDelete = useCallback(async () => {
    if (!pendingDelete || pendingDelete.length === 0) return;

    try {
      for (const target of pendingDelete) {
        await deleteMutation.mutateAsync({ path: target.path, recursive: target.isDirectory });
      }
    } finally {
      setSelectedFiles([]);
      setDropdownFile(null);
      setDropdownPos(null);
      setPendingDelete(null);
    }
  }, [pendingDelete, deleteMutation]);

  // Handle create folder
  const handleCreateFolder = useCallback(() => {
    if (!newFolderName.trim()) return;
    const dirPath = currentPath ? `${currentPath}/${newFolderName.trim()}` : newFolderName.trim();
    createDirMutation.mutate(dirPath, {
      onSuccess: () => {
        setNewFolderName('');
        setShowNewFolderModal(false);
      },
    });
  }, [currentPath, newFolderName, createDirMutation]);

  // Parse breadcrumb path
  const pathParts = currentPath.split('/').filter(Boolean);
  const breadcrumbs = pathParts.map((part, index) => ({
    name: part,
    path: pathParts.slice(0, index + 1).join('/'),
  }));

  // Derived selection helpers
  const selectedFileSet = useMemo(() => new Set(selectedFiles.map((f) => f.path)), [selectedFiles]);
  const singleSelected = selectedFiles.length === 1 ? selectedFiles[0] : null;
  const downloadableCount = selectedFiles.filter((f) => !f.isDirectory).length;

  // Helper to open delete modal with captured targets
  const openDeleteModal = useCallback(
    (targets?: VolumePathItem[]) => {
      setPendingDelete(targets || (dropdownFile ? [dropdownFile] : selectedFiles));
    },
    [dropdownFile, selectedFiles]
  );

  return (
    <div
      ref={dropZoneRef}
      className={`relative flex flex-col bg-white dark:bg-[#0a0a0a] rounded-lg border-2 transition-colors ${
        isDragging
          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
          : 'border-gray-200 dark:border-[#2a2a2a]'
      }`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Drag overlay */}
      {isDragging && !dragOverFolder && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-primary-500/10 dark:bg-primary-500/20 pointer-events-none">
          <div className="flex flex-col items-center gap-2 p-6 bg-white dark:bg-[#161616] rounded-lg shadow-lg border-2 border-dashed border-primary-500">
            <Upload className="h-8 w-8 text-primary-500" />
            <p className="text-sm font-medium text-primary-600 dark:text-primary-400">
              Drop files to upload to {currentPath || '/'}
            </p>
          </div>
        </div>
      )}

      {/* Header / Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-50 dark:bg-[#161616] border-b border-gray-200 dark:border-[#2a2a2a]">
        <div className="flex items-center gap-2">
          <FolderOpen className="h-4 w-4 text-primary-500" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            File Explorer
          </span>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadMutation.isPending}
            title="Upload file"
          >
            {uploadMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )}
          </Button>
          <button
            onClick={() => setOverwrite((prev) => !prev)}
            className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md border transition-colors cursor-pointer ${
              overwrite
                ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-400 dark:border-primary-600 text-primary-700 dark:text-primary-300 font-medium'
                : 'bg-white dark:bg-[#161616] border-gray-300 dark:border-[#333] text-gray-400 dark:text-gray-500 hover:border-gray-400 dark:hover:border-gray-500'
            }`}
            title={
              overwrite
                ? 'Overwrite is ON — existing files will be replaced'
                : 'Overwrite is OFF — existing files will be skipped'
            }
          >
            {overwrite ? (
              <CheckCircle className="h-3.5 w-3.5" />
            ) : (
              <Circle className="h-3.5 w-3.5" />
            )}
            Overwrite
          </button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowNewFolderModal(true)}
            title="New folder"
          >
            <FolderPlus className="h-4 w-4" />
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => handleDownload()}
            disabled={downloadableCount === 0 || isDownloading}
            title={
              downloadableCount > 1
                ? `Download ${downloadableCount} files`
                : 'Download selected file'
            }
          >
            {isDownloading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => openDeleteModal()}
            disabled={selectedFiles.length === 0}
            title={
              selectedFiles.length > 1 ? `Delete ${selectedFiles.length} items` : 'Delete selected'
            }
          >
            <Trash2 className="h-4 w-4" />
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => refetch()}
            disabled={isLoading}
            title="Refresh"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Breadcrumb Navigation */}
      <div className="flex items-center gap-1 px-4 py-2 bg-gray-100 dark:bg-[#161616]/50 border-b border-gray-200 dark:border-[#2a2a2a] overflow-x-auto">
        <button
          onClick={() => navigateTo('')}
          className="text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
        >
          /
        </button>
        {breadcrumbs.map((crumb, index) => (
          <div key={crumb.path} className="flex items-center">
            <ChevronRight className="h-4 w-4 text-gray-400 dark:text-gray-600 mx-1" />
            <button
              onClick={() => navigateTo(crumb.path)}
              className={`text-sm ${
                index === breadcrumbs.length - 1
                  ? 'text-gray-900 dark:text-gray-100 font-medium'
                  : 'text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400'
              }`}
            >
              {crumb.name}
            </button>
          </div>
        ))}
      </div>

      {/* Upload progress bar */}
      {uploadProgress && (
        <div className="px-4 py-2 border-b border-gray-200 dark:border-[#2a2a2a] bg-gray-50 dark:bg-[#161616]">
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
              <Loader2 className="h-3 w-3 animate-spin text-gray-400" />
              <span className="font-medium">Uploading</span>
              <span className="truncate max-w-[200px] text-gray-500 dark:text-gray-400">
                {uploadProgress.currentFileName}
              </span>
            </div>
            <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
              {uploadProgress.bytesTotal > 0 && (
                <span>
                  {formatFileSize(uploadProgress.bytesLoaded)} /{' '}
                  {formatFileSize(uploadProgress.bytesTotal)}
                </span>
              )}
              {uploadProgress.fileCount > 1 && (
                <span>
                  File {uploadProgress.fileIndex + 1} of {uploadProgress.fileCount}
                </span>
              )}
              <button
                onClick={() => uploadAbortRef.current?.abort()}
                className="p-0.5 rounded-sm hover:bg-gray-200 dark:hover:bg-[#333] text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                title="Cancel upload"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
          <div className="w-full bg-gray-200 dark:bg-[#2a2a2a] rounded-full h-1">
            <div
              className="bg-primary-500 dark:bg-primary-400 h-1 rounded-full transition-all duration-150"
              style={{
                width: `${uploadProgress.bytesTotal > 0 ? (uploadProgress.bytesLoaded / uploadProgress.bytesTotal) * 100 : 0}%`,
              }}
            />
          </div>
        </div>
      )}

      {/* Upload error bar */}
      {uploadError && (
        <div className="flex items-center gap-2 px-4 py-2 border-b border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-950/20">
          <AlertCircle className="h-3.5 w-3.5 text-red-500 dark:text-red-400 shrink-0" />
          <span className="text-xs text-red-700 dark:text-red-300 flex-1">{uploadError}</span>
          <button
            onClick={() => setUploadError(null)}
            className="p-0.5 rounded-sm hover:bg-red-100 dark:hover:bg-red-900/30 text-red-400 hover:text-red-600 dark:hover:text-red-300 transition-colors shrink-0"
            title="Dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* File List + Sidebar */}
      <div className="flex h-[520px]">
        <div className="flex-1 flex flex-col min-w-0">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500 dark:text-gray-400 p-4">
              <AlertCircle className="h-8 w-8 mb-2 text-red-400" />
              <p className="text-sm text-center">Failed to load files</p>
              <p className="text-xs text-gray-400 dark:text-gray-400 mt-1">
                {(error as Error).message}
              </p>
              <Button variant="secondary" size="sm" onClick={() => refetch()} className="mt-4">
                Try Again
              </Button>
            </div>
          ) : sortedEntries.length === 0 && !currentPath ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500 dark:text-gray-400 p-4">
              <Upload className="h-8 w-8 mb-2 opacity-50" />
              <p className="text-sm">This folder is empty</p>
              <p className="text-xs text-gray-400 dark:text-gray-400 mt-1">
                Drag and drop files here to upload
              </p>
            </div>
          ) : (
            <>
              {/* Fixed header */}
              <table className="w-full table-fixed border-separate border-spacing-0">
                <colgroup>
                  <col className="w-8" />
                  <col />
                  <col className="w-24" />
                  <col className="w-40" />
                  <col className="w-10" />
                </colgroup>
                <thead className="bg-gray-50 dark:bg-[#161616] text-xs text-gray-500 dark:text-gray-400 uppercase">
                  <tr>
                    <th className="px-2 py-2 border-b border-gray-200 dark:border-[#2a2a2a]" />
                    <th
                      className="text-left font-medium px-4 py-2 border-b border-gray-200 dark:border-[#2a2a2a] cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-300"
                      onClick={() => toggleSort('name')}
                    >
                      <span className="inline-flex items-center gap-1">
                        Name
                        {sortColumn === 'name' &&
                          (sortDirection === 'asc' ? (
                            <ArrowUp className="h-3 w-3" />
                          ) : (
                            <ArrowDown className="h-3 w-3" />
                          ))}
                      </span>
                    </th>
                    <th
                      className="text-right px-4 py-2 font-medium border-b border-gray-200 dark:border-[#2a2a2a] cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-300"
                      onClick={() => toggleSort('size')}
                    >
                      <span className="inline-flex items-center justify-end gap-1">
                        Size
                        {sortColumn === 'size' &&
                          (sortDirection === 'asc' ? (
                            <ArrowUp className="h-3 w-3" />
                          ) : (
                            <ArrowDown className="h-3 w-3" />
                          ))}
                      </span>
                    </th>
                    <th
                      className="text-right px-4 py-2 font-medium border-b border-gray-200 dark:border-[#2a2a2a] cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-300"
                      onClick={() => toggleSort('modified')}
                    >
                      <span className="inline-flex items-center justify-end gap-1">
                        Modified
                        {sortColumn === 'modified' &&
                          (sortDirection === 'asc' ? (
                            <ArrowUp className="h-3 w-3" />
                          ) : (
                            <ArrowDown className="h-3 w-3" />
                          ))}
                      </span>
                    </th>
                    <th className="px-2 py-2 border-b border-gray-200 dark:border-[#2a2a2a]" />
                  </tr>
                </thead>
              </table>
              {/* Scrollable body */}
              <div className="flex-1 overflow-auto min-h-0">
                <table className="w-full table-fixed border-separate border-spacing-0">
                  <colgroup>
                    <col className="w-8" />
                    <col />
                    <col className="w-24" />
                    <col className="w-40" />
                    <col className="w-10" />
                  </colgroup>
                  <tbody>
                    {currentPath && (
                      <tr
                        onClick={navigateUp}
                        className="cursor-pointer transition-colors [&>td]:border-b [&>td]:border-gray-100 dark:[&>td]:border-[#2a2a2a] hover:bg-gray-50 dark:hover:bg-[#161616]/50"
                      >
                        <td className="px-2 py-2" />
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-2">
                            <FolderOpen className="h-4 w-4 text-yellow-500" />
                            <span className="text-sm text-gray-400 dark:text-gray-500 truncate">
                              [..]
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-2 text-right">
                          <span className="text-sm text-gray-500 dark:text-gray-400">-</span>
                        </td>
                        <td className="px-4 py-2 text-right">
                          <span className="text-sm text-gray-500 dark:text-gray-400">-</span>
                        </td>
                        <td className="px-2 py-2 relative">
                          <button
                            className="invisible p-0.5 rounded-sm border border-gray-200 dark:border-[#333] bg-white dark:bg-[#1a1a1a]"
                            tabIndex={-1}
                            aria-hidden
                          >
                            <MoreHorizontal className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      </tr>
                    )}
                    {sortedEntries.map((entry) => (
                      <tr
                        key={entry.path}
                        onClick={(e) => handleEntryClick(entry, e)}
                        onDoubleClick={() => entry.isDirectory && navigateTo(entry.path)}
                        onDragOver={
                          entry.isDirectory ? (e) => handleFolderDragOver(e, entry.path) : undefined
                        }
                        onDragLeave={entry.isDirectory ? handleFolderDragLeave : undefined}
                        onDrop={
                          entry.isDirectory ? (e) => handleFolderDrop(e, entry.path) : undefined
                        }
                        className={`cursor-pointer transition-colors [&>td]:border-b [&>td]:border-gray-100 dark:[&>td]:border-[#2a2a2a] ${
                          dragOverFolder === entry.path
                            ? 'bg-primary-100 dark:bg-primary-900/40 ring-2 ring-primary-500 ring-inset'
                            : selectedFileSet.has(entry.path) || dropdownFile?.path === entry.path
                              ? 'bg-primary-50 dark:bg-primary-900/20'
                              : 'hover:bg-gray-50 dark:hover:bg-[#161616]/50'
                        }`}
                      >
                        <td className="px-2 py-2">
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              const itemIndex = sortedEntries.findIndex(
                                (el) => el.path === entry.path
                              );
                              setSelectedFiles((prev) => {
                                const exists = prev.some((f) => f.path === entry.path);
                                return exists
                                  ? prev.filter((f) => f.path !== entry.path)
                                  : [...prev, entry];
                              });
                              lastClickedIndexRef.current = itemIndex;
                              setDropdownFile(null);
                              setDropdownPos(null);
                            }}
                            className={`flex items-center justify-center w-4 h-4 rounded-full border cursor-pointer transition-colors ${
                              selectedFileSet.has(entry.path)
                                ? 'bg-primary-500 border-primary-500 text-white'
                                : 'border-gray-300 dark:border-[#444] hover:border-gray-400 dark:hover:border-gray-500'
                            }`}
                          >
                            {selectedFileSet.has(entry.path) && (
                              <Check className="h-3 w-3" strokeWidth={3} />
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-2">
                            {getFileIcon(entry)}
                            <span className="text-sm text-gray-900 dark:text-gray-100 truncate">
                              {entry.itemName}
                            </span>
                            {entry.isDirectory && dragOverFolder === entry.path && (
                              <span className="text-xs text-primary-600 dark:text-primary-400 font-medium">
                                Drop to upload here
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-2 text-right">
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {entry.isDirectory ? '-' : formatFileSize(entry.sizeBytes)}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right">
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {formatDate(entry.lastModifiedUtc)}
                          </span>
                        </td>
                        <td className="px-2 py-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (dropdownFile?.path === entry.path) {
                                setDropdownFile(null);
                                setDropdownPos(null);
                              } else {
                                const rect = (
                                  e.currentTarget as HTMLElement
                                ).getBoundingClientRect();
                                setDropdownPos({
                                  top: rect.bottom + 4,
                                  right: window.innerWidth - rect.right,
                                });
                                setDropdownFile(entry);
                              }
                            }}
                            className={`p-0.5 rounded border transition-colors ${
                              dropdownFile?.path === entry.path
                                ? 'border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                                : 'border-gray-200 dark:border-[#333] bg-white dark:bg-[#1a1a1a] hover:bg-gray-50 dark:hover:bg-[#222] text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
                            }`}
                            title="Actions"
                            aria-label={`Actions for ${entry.itemName}`}
                          >
                            <MoreHorizontal className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* File Detail Sidebar */}
        <div
          className={`border-l border-gray-200 dark:border-[#2a2a2a] bg-gray-50 dark:bg-[#111] shrink-0 overflow-hidden transition-[width] duration-200 ease-in-out ${
            selectedFiles.length > 0 ? 'w-64' : 'w-0 border-l-0'
          }`}
        >
          {singleSelected && (
            <div className="w-64 flex flex-col h-full">
              {/* Sidebar Header */}
              <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 dark:border-[#2a2a2a]">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="shrink-0">{getFileIcon(singleSelected)}</span>
                  <span
                    className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate"
                    title={singleSelected.itemName}
                  >
                    {singleSelected.itemName}
                  </span>
                </div>
                <button
                  onClick={() => setSelectedFiles([])}
                  className="p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 shrink-0"
                  title="Close"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>

              {/* Metadata */}
              <div className="px-3 py-3 space-y-3 text-xs">
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">Type</dt>
                  <dd className="mt-0.5 text-gray-900 dark:text-gray-200">
                    {singleSelected.isDirectory
                      ? 'Directory'
                      : singleSelected.contentType || 'Unknown'}
                  </dd>
                </div>
                {!singleSelected.isDirectory && (
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400">Size</dt>
                    <dd className="mt-0.5 text-gray-900 dark:text-gray-200">
                      {formatFileSize(singleSelected.sizeBytes)}
                    </dd>
                  </div>
                )}
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">Modified</dt>
                  <dd className="mt-0.5 text-gray-900 dark:text-gray-200">
                    {formatDate(singleSelected.lastModifiedUtc)}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">Path</dt>
                  <dd className="mt-0.5 text-gray-900 dark:text-gray-200 font-mono break-all">
                    /{singleSelected.path}
                  </dd>
                </div>
              </div>

              {/* Sidebar Actions */}
              <div className="mt-auto border-t border-gray-200 dark:border-[#2a2a2a] p-3 space-y-0.5">
                {!singleSelected.isDirectory && (
                  <button
                    className="w-full flex items-center gap-2 px-2.5 py-1.5 text-sm rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#2a2a2a] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    onClick={() => handleDownload()}
                    disabled={isDownloading}
                  >
                    {isDownloading ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Download className="h-3.5 w-3.5" />
                    )}
                    Download
                  </button>
                )}
                <button
                  className="w-full flex items-center gap-2 px-2.5 py-1.5 text-sm rounded-md text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                  onClick={() => openDeleteModal()}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete
                </button>
              </div>
            </div>
          )}
          {selectedFiles.length > 1 && (
            <div className="w-64 flex flex-col h-full">
              {/* Multi-select Header */}
              <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 dark:border-[#2a2a2a]">
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {selectedFiles.length} items selected
                </span>
                <button
                  onClick={() => setSelectedFiles([])}
                  className="p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 shrink-0"
                  title="Clear selection"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>

              {/* Summary */}
              <div className="px-3 py-3 space-y-3 text-xs">
                {selectedFiles.some((f) => !f.isDirectory) && (
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400">Files</dt>
                    <dd className="mt-0.5 text-gray-900 dark:text-gray-200">
                      {selectedFiles.filter((f) => !f.isDirectory).length}
                    </dd>
                  </div>
                )}
                {selectedFiles.some((f) => f.isDirectory) && (
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400">Folders</dt>
                    <dd className="mt-0.5 text-gray-900 dark:text-gray-200">
                      {selectedFiles.filter((f) => f.isDirectory).length}
                    </dd>
                  </div>
                )}
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">Total Size</dt>
                  <dd className="mt-0.5 text-gray-900 dark:text-gray-200">
                    {formatFileSize(selectedFiles.reduce((sum, f) => sum + (f.sizeBytes || 0), 0))}
                  </dd>
                </div>
              </div>

              {/* Multi Actions */}
              <div className="mt-auto border-t border-gray-200 dark:border-[#2a2a2a] p-3 space-y-0.5">
                {downloadableCount > 0 && (
                  <button
                    className="w-full flex items-center gap-2 px-2.5 py-1.5 text-sm rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#2a2a2a] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    onClick={() => handleDownload()}
                    disabled={isDownloading}
                  >
                    {isDownloading ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Download className="h-3.5 w-3.5" />
                    )}
                    Download {downloadableCount} files
                  </button>
                )}
                <button
                  className="w-full flex items-center gap-2 px-2.5 py-1.5 text-sm rounded-md text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                  onClick={() => openDeleteModal()}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete {selectedFiles.length} items
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Hidden file input for uploads */}
      <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleUpload} />

      {/* Click-away listener + dropdown portal */}
      {dropdownFile &&
        dropdownPos &&
        createPortal(
          <>
            <div
              className="fixed inset-0 z-9998"
              onClick={() => {
                setDropdownFile(null);
                setDropdownPos(null);
              }}
            />
            <div
              className="fixed w-64 bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-[#333] rounded-lg shadow-lg z-9999"
              style={{ top: dropdownPos.top, right: dropdownPos.right }}
            >
              {/* Actions */}
              <div className="py-1">
                {!dropdownFile.isDirectory && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownload(dropdownFile);
                    }}
                    disabled={isDownloading}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#222] transition-colors disabled:opacity-50"
                  >
                    {isDownloading ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Download className="h-3.5 w-3.5" />
                    )}
                    Download
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openDeleteModal([dropdownFile]);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete
                </button>
              </div>
            </div>
          </>,
          document.body
        )}

      {/* New Folder Modal */}
      <Modal
        isOpen={showNewFolderModal}
        onClose={() => {
          setShowNewFolderModal(false);
          setNewFolderName('');
        }}
        title="Create New Folder"
        size="sm"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setShowNewFolderModal(false);
                setNewFolderName('');
              }}
              disabled={createDirMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateFolder}
              isLoading={createDirMutation.isPending}
              disabled={!newFolderName.trim()}
            >
              Create
            </Button>
          </>
        }
      >
        <Input
          label="Folder Name"
          placeholder="new-folder"
          value={newFolderName}
          onChange={(e) => setNewFolderName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
          autoFocus
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={pendingDelete !== null && pendingDelete.length > 0}
        onClose={() => setPendingDelete(null)}
        onConfirm={handleDelete}
        title={
          pendingDelete && pendingDelete.length > 1
            ? `Delete ${pendingDelete.length} Items`
            : `Delete ${pendingDelete?.[0]?.isDirectory ? 'Folder' : 'File'}`
        }
        message={
          pendingDelete && pendingDelete.length > 1
            ? `Are you sure you want to delete ${pendingDelete.length} items? This action cannot be undone.`
            : `Are you sure you want to delete "${pendingDelete?.[0]?.itemName}"?${pendingDelete?.[0]?.isDirectory ? ' This will delete all contents recursively.' : ''}`
        }
        confirmText="Delete"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
